import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import { reactive, watch, ref, computed } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import useAlerts from './useAlerts';
import useCurrentUser from './useCurrentUser';
import useUsersQuery from './useUsersQuery';
import useSingleLeadQuery from './useSingleLeadQuery';

const { user } = useCurrentUser();
const { salespeople } = useUsersQuery();
const { decryptSymbols } = useDecryptSymbols();
const { addAlert } = useAlerts();
const lead = useSingleLeadQuery.lead;

const leads = reactive([]);
const filters = reactive({
  complete: false,
  dateFrom: null,
  dateTo: null,
  salespersonId: null,
  delivery: false,
  location: 'All',
  scores: ['hot', 'warm', 'cool'], // Default to show hot, warm, and cool but not dead
});

// If this is updated make sure to update the server side socket query as well
const GET_LEADS = gql`
  query LeadsQuery($salespersonId: String, $complete: Boolean, $dateFrom: String, $dateTo: String, $delivery: Boolean, $scores: [String]) {
    leads(salespersonId: $salespersonId, complete: $complete, dateFrom: $dateFrom, dateTo: $dateTo, delivery: $delivery, scores: $scores) {
      LEADID
      ASSIGNEDID
      NAME
      NOTES
      BWQUOTE
      CREATEDATE
      JOBNAME
      SOURCE
      LINKMETHOD
      COMPLETE
      INVNUM
      AMOUNT
      COMPLETEDATE
      COMPANYID
      CONTACTID
      HASDELIVERY
      DELIVERYADDRESS
      DELIVERYDATE
      GATECODE
      CHANNEL
      ALERTDELAY
      DELIVERYNOTES
      QUOTESUMMARY
      QUOTEDETAILS
      SCORE
      SUGGESTEDSALESPERSON
      address {
        LINE1
        LINE2
        CITY
        STATE
        POSTALCODE
      }
      email {
        ADDRESS
      }
      phones {
        NUMBERDISPLAY
        TYPEID
        EXT
      }
      interactions {
        INTERACTIONID
        DATE
        NOTE
        TYPE
        SALESPERSONID
      }
      company {
        NAME
        address {
          LINE1
          LINE2
          CITY
          STATE
          POSTALCODE
        }
        phones {
          NUMBERDISPLAY
          TYPEID
          EXT
        }
      }
      contact {
        FIRSTNAME
        LASTNAME
        address {
          LINE1
          LINE2
          CITY
          STATE
          POSTALCODE
        }
        company {
          NAME
        }
        email {
          ADDRESS
        }
        phones {
          NUMBERDISPLAY
          EXT
          TYPEID
        }
      }
    }
  }
`;

const refetchLeads = ref(null);
const loadingLeads = ref(false);

function setSalesperson() {
  if (user.userid && user.insideSales) {
    filters.salespersonId = user.userid;
  } else {
    filters.salespersonId = null;
  }
}

setSalesperson();

watch(
  user,
  () => {
    setSalesperson();
  },
  { deep: true }
);

watch(filters, () => {
  runFilters();
});

watch(leads, () => {
  runFilters();
});

const salespersonLocations = computed(() => {
  const locations = {};

  salespeople.forEach((person) => {
    if (!locations[person.LOCATION]) {
      locations[person.LOCATION] = [];
    }
    locations[person.LOCATION].push(person);
  });
  return locations;
});

function runFilters() {
  leads.forEach((lead, index) => {
    if (lead.COMPLETE && !filters.complete) {
      leads.splice(index, 1);
      return;
    }

    if (filters.dateFrom && new Date(filters.dateFrom) > new Date(parseInt(lead.CREATEDATE) || lead.CREATEDATE)) {
      leads.splice(index, 1);
      return;
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setDate(dateTo.getDate() + 1); // add 1 day
      if (dateTo < new Date(parseInt(lead.CREATEDATE) || lead.CREATEDATE)) {
        leads.splice(index, 1);
        return;
      }
    }

    if (user.leadsAdmin) {
      if (filters.salespersonId && lead.ASSIGNEDID != filters.salespersonId && lead.ASSIGNEDID) {
        leads.splice(index, 1);
        return;
      }
    } else {
      if (filters.salespersonId && lead.ASSIGNEDID != filters.salespersonId) {
        leads.splice(index, 1);
        return;
      }
    }

    if (filters.location != 'All' && lead.ASSIGNEDID && !salespersonLocations.value[filters.location]?.some((person) => person.USERID == lead.ASSIGNEDID)) {
      leads.splice(index, 1);
      return;
    }

    if (filters.delivery && !lead.HASDELIVERY) {
      leads.splice(index, 1);
      return;
    }

    if (filters.scores && filters.scores.length > 0 && !filters.scores.includes(lead.SCORE)) {
      leads.splice(index, 1);
      return;
    }
  });
}

const useLeadsQuery = function () {
  const { result, error, refetch, loading } = useQuery(
    GET_LEADS,
    () => ({
      ...filters,
    }),
    { fetchPolicy: 'no-cache' }
  );

  refetchLeads.value = refetch;
  loadingLeads.value = loading;

  watch(result, () => {
    if (result.value) {
      const decrypted = decryptSymbols(result.value?.leads);
      leads.splice(0, leads.length, ...decrypted);
    }
  });

  watch(error, () => {
    if (error.value) {
      console.error('Error fetching leads:', error.value);
      addAlert('Error fetching leads', 'high', 4);
    }
  });
};

const leadsIncludingSingleLead = computed(() => {
  if (lead.value && !leads.some((l) => l.LEADID == lead.value.LEADID)) {
    return [...leads, lead.value];
  }
  return leads;
});

export default {
  useLeadsQuery,
  leadsIncludingSingleLead,
  leads,
  refetchLeads,
  loadingLeads,
  filters,
};
